import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bug,
  ImagePlus,
  Loader2,
  Send,
  Trash2,
  Video,
  X,
} from 'lucide-react';
import { API_BASE_URL } from '../apiBase';
import { formatApiError } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import OfficeModalHeader from './office/OfficeModalHeader';
import { cn } from '../lib/utils';

const MAX_FILES = 4;
const IMAGE_MAX = 5 * 1024 * 1024;
const VIDEO_MAX = 40 * 1024 * 1024;

function fileKind(file) {
  const t = String(file?.type || '').toLowerCase();
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('image/')) return 'image';
  return '';
}

export default function BugReportModal({
  open,
  onOpenChange,
  buildVersion,
  submitUrl,
  getAuthHeaders,
  successMessage,
}) {
  const inputRef = useRef(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('normal');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const previews = useMemo(
    () =>
      files.map((f) => ({
        name: f.name,
        kind: fileKind(f),
        url: URL.createObjectURL(f),
      })),
    [files],
  );

  const reset = () => {
    setTitle('');
    setDescription('');
    setSeverity('normal');
    setFiles([]);
  };

  const addFiles = (list) => {
    const next = [...files];
    for (const f of Array.from(list || [])) {
      if (next.length >= MAX_FILES) break;
      const kind = fileKind(f);
      if (!kind) {
        toast.error(`Tipo não suportado: ${f.name}`);
        continue;
      }
      if (kind === 'image' && f.size > IMAGE_MAX) {
        toast.error(`Imagem demasiado grande: ${f.name} (máx. 5 MB)`);
        continue;
      }
      if (kind === 'video' && f.size > VIDEO_MAX) {
        toast.error(`Vídeo demasiado grande: ${f.name} (máx. 40 MB)`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (description.trim().length < 8) {
      toast.error('Descreva o problema com pelo menos 8 caracteres');
      return;
    }
    setSending(true);
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('description', description.trim());
      form.append('severity', severity);
      form.append('category', 'bug');
      form.append('page_url', window.location.href);
      form.append('app_version', buildVersion || '');
      files.forEach((f) => form.append('files', f));
      const endpoint = submitUrl || `${API_BASE_URL}/api/bug-reports`;
      const extraHeaders = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
      const { data } = await axios.post(endpoint, form, {
        withCredentials: !submitUrl,
        headers: { ...extraHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(
        successMessage || 'Reporte enviado. A equipe recebe no Telegram com os detalhes.',
      );
      reset();
      onOpenChange?.(false);
      return data;
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          e?.message ||
          'Não foi possível enviar o reporte',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v && !sending) {
          onOpenChange?.(false);
        } else if (v) {
          onOpenChange?.(true);
        }
      }}
    >
      <DialogContent className="flex max-h-[92vh] max-w-lg flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <OfficeModalHeader
          icon={Bug}
          title="Reportar bug"
          description="Descreva o problema e anexe capturas ou um vídeo curto. O reporte vai direto para a equipe Dragon Tech."
          className="office-modal-header shrink-0"
        />

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div>
            <Label htmlFor="bug-title" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Título (opcional)
            </Label>
            <Input
              id="bug-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Erro ao renovar cliente"
              className="mt-1.5 h-10"
              maxLength={200}
            />
          </div>

          <div>
            <Label htmlFor="bug-desc" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              O que aconteceu?
            </Label>
            <textarea
              id="bug-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={20000}
              placeholder="Passos para reproduzir, o que esperava e o que viu…"
              className="mt-1.5 w-full resize-y rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Prioridade
            </Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {[
                { id: 'low', label: 'Baixa' },
                { id: 'normal', label: 'Normal' },
                { id: 'high', label: 'Alta' },
                { id: 'critical', label: 'Crítica' },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSeverity(s.id)}
                  className={cn(
                    'h-8 rounded-full border px-3 text-xs font-semibold',
                    severity === s.id
                      ? 'border-primary/40 bg-primary/15 text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Anexos
            </Label>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mt-1.5 flex w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-muted/20 px-4 py-6 text-center transition hover:border-primary/40 hover:bg-primary/5"
            >
              <div className="flex items-center gap-2 text-primary">
                <ImagePlus className="h-5 w-5" />
                <Video className="h-5 w-5" />
              </div>
              <p className="text-sm font-semibold text-foreground">Adicionar imagem ou vídeo</p>
              <p className="text-xs text-muted-foreground">Até {MAX_FILES} arquivos · imagens 5 MB · vídeos 40 MB</p>
            </button>

            {previews.length > 0 ? (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {previews.map((p, idx) => (
                  <div key={`${p.name}-${idx}`} className="relative overflow-hidden rounded-lg border border-border bg-black/40">
                    {p.kind === 'video' ? (
                      <video src={p.url} className="h-28 w-full object-cover" muted />
                    ) : (
                      <img src={p.url} alt="" className="h-28 w-full object-cover" />
                    )}
                    <button
                      type="button"
                      className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/70 text-white"
                      onClick={() => removeFile(idx)}
                      title="Remover"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <p className="truncate px-2 py-1 text-[10px] text-white/80">{p.name}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="outline"
            className="h-10 rounded-xl"
            disabled={sending}
            onClick={() => onOpenChange?.(false)}
          >
            <X className="mr-1.5 h-4 w-4" />
            Cancelar
          </Button>
          <Button type="button" className="h-10 rounded-xl" disabled={sending} onClick={submit}>
            {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
            {sending ? 'A enviar…' : 'Enviar reporte'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
